import React from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { makeStyles } from '@mui/styles'

import { Mention, MentionsInput } from 'react-mentions'

import getGlobal from '~/src/Lib/getGlobal'

import Formatted from './SmartText'

const isMacOS = (/^mac/i).test(getGlobal().navigator?.platform)

const defaultPlaceholder = `type @ to mention, ${isMacOS ? '\u2318' : 'control'} + enter to send`

const useStyles = makeStyles(theme => ({
  '@global': {
    '.aroya__suggestions': {
      minWidth: 'inherit !important',
      backgroundColor: 'transparent !important',
      transform: 'translate3d(0, 1rem, 0)',
    },
    '.aroya__suggestions__list': {
      overflowX: 'auto',
      height: '100%',
      maxHeight: '30vh',
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[5],
    },
    '.aroya__suggestions__item': {
      padding: '8px 16px !important',
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
      fontSize: '0.875rem',
      fontWeight: '500',
      '&:hover': {
        backgroundColor: theme.palette.hovered,
      }
    },
  },
  wrap: {
    flex: 1,
    '& .aroya__control': {
      position: 'relative',
      minHeight: '4rem',
      ...theme.typography.body1,
    },
    '& .aroya__input, & .aroya__highlighter': {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      padding: '0.5rem',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
    },
    '& .aroya__input': {
      caretColor: theme.palette.white.muted,
      color: 'transparent',
      borderRadius: theme.shape.borderRadius,
      borderColor: theme.palette.contrast.inactive,
      outline: 'none',
      '&:hover': {
        borderColor: theme.palette.contrast.muted,
      },
      '&:focus': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .aroya__highlighter > *': {
      fontSize: 'inherit !important',
      lineHeight: 'inherit !important',
      letterSpacing: 'inherit !important',
    },
    '& .aroya__highlighter__substring': {
      color: theme.palette.text.primary,
      visibility: 'visible !important',
    },
    '& .aroya__mention': {
      color: theme.palette.primary.main,
    },
    '&$disabled': {
      pointerEvents: 'none',
      '& .aroya__input': {
        borderColor: theme.palette.contrast.disabled,
      },
      '& .aroya__highlighter__substring': {
        color: theme.palette.contrast.disabled,
      },
    }
  },
  disabled: {},
  mentioned: {
    color: theme.palette.primary.main,
    fontSize: '14px',
    fontWeight: 'bold !important',
  }
}))

/**
 * Returns a formatted text or editable comment with user mentions highlighted
 * @component
 * @alias MentionField
 * @named_export Component
 * @composes
 * name: MentionInput
 * from: react-mentions
 * link: https://github.com/signavio/react-mentions#configuration
 * @example
 * {() => {
 *   const mentionable = [
 *     { id: 1, display: 'Bill Williams' },
 *     { id: 2, display: 'Joe Schmoe' },
 *     { id: 3, display: 'Dwayne "The Rock" Johnson' }
 *   ]
 *   const [text, setText] = React.useState('')
 *
 *   return (
 *     <div className="default-display">
 *       <Box className="default-display" flex="1">
 *          input:{
 *            '\u00a0'
 *          }<MentionField
 *            data={mentionable}
 *            placeholder="type @"
 *            value={text}
 *            onChange={e => setText(e.target.value)}
 *          />
 *       </Box>
 *       <Box className="default-display" flex="1">
 *         text: <MentionField asText value={text} />
 *       </Box>
 *     </div>
 *   )
 * }}
 *
 *
 */
export const Component = ({
  data,
  disabled = false,
  asText = false,
  multiline = null,
  placeholder = defaultPlaceholder,
  value = '',
  onAdd = undefined,
  onSave = undefined,
  ...restProps
}) => {
  const classes = useStyles()

  const handleSaveKeyDown = React.useCallback(({ key, ctrlKey, metaKey }) => {
    if (key === 'Enter' && (ctrlKey || (isMacOS && metaKey))) {
      onSave()
    }
  }, [onSave])

  if (asText) {
    return <Formatted text={value} />
  }
  const placeholderText = placeholder.match(/@.*(ctrl|\u{2318})/u)
    ? placeholder
    : `${placeholder} (${defaultPlaceholder})`

  return (
    <div className={classNames(classes.wrap, disabled && classes.disabled)}>
      <MentionsInput
        {...restProps}
        aria-label={placeholderText}
        className="aroya"
        disabled={disabled}
        placeholder={placeholderText}
        singleLine={multiline === false}
        suggestionsPortalHost={document.getElementById('mentionsRoot')}
        value={value}
        onKeyDown={handleSaveKeyDown}
      >
        <Mention
          trigger="@"
          className="aroya__mention"
          data={data}
          onAdd={onAdd}
          appendSpaceOnAdd
          displayTransform={(_, label) => `@${label}`}
        />
      </MentionsInput>
    </div>
  )
}
Component.displayName = 'MentionField'
Component.propTypes = {
  asText: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    display: PropTypes.node
  })).isRequired,
  disabled: PropTypes.bool,
  multiline: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onAdd: PropTypes.func,
  onSave: PropTypes.func,
}

const Memoized = React.memo(props => {
  const { mentionable } = useConnect('selectMentionable')

  return <Component {...props} data={mentionable} />
})

export default Memoized
